// src/components/AiKeyCard.tsx
//
// Shown to a parent on an Explorer's (9–12) Learn page. The FSL AI key lets
// the child's own web pages call the FSL AI endpoint in the Quarter 3 AI
// lessons. The real AI provider key stays on our server; this key works only
// for that endpoint, with a daily limit, and can be replaced or removed.

import { useState } from "react";
import {
  errorText,
  useCreateAiKeyMutation,
  useGetAiKeysQuery,
  useRemoveAiKeyMutation,
} from "../app/api/learningApi";

import { Bot, Check } from "lucide-react";
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/+$/, "");

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-NG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : null;

export default function AiKeyCard({ childId }: { childId: string }) {
  const { data } = useGetAiKeysQuery();
  const [create, { isLoading: creating }] = useCreateAiKeyMutation();
  const [remove, { isLoading: removing }] = useRemoveAiKeyMutation();
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const row = data?.data.find((r) => r.childId === childId);
  if (!row?.eligible) return null;

  const make = async () => {
    if (row.key && !window.confirm(`Replace ${row.firstName}'s key? The old one stops working straight away.`)) return;
    setNote(null);
    try {
      const res = await create(childId).unwrap();
      setNewKey(res.data.newKey ?? null);
      setCopied(false);
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not create the key") });
    }
  };

  const drop = async () => {
    if (!window.confirm(`Remove ${row.firstName}'s key? Their AI projects will stop working until you create a new one.`)) return;
    setNote(null);
    try {
      const res = await remove(childId).unwrap();
      setNewKey(null);
      setNote({ ok: true, text: res.message });
    } catch (e) {
      setNote({ ok: false, text: errorText(e, "Could not remove the key") });
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newKey ?? "");
      setCopied(true);
    } catch {
      /* the key is selectable on screen */
    }
  };

  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5">
      <p className="text-sm font-bold text-[#16213e]"><Bot className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{row.firstName}'s FSL AI key</p>
      <p className="mt-1 text-sm leading-6 text-[#5f6780]">
        In the Quarter 3 AI lessons, {row.firstName}'s own web pages ask an AI to explain, summarise or judge the
        feeling of some text. They use this key, which works only for that, up to {row.dailyLimit} times a day. The
        real AI account stays with Future Smart Lab.
      </p>
      {!row.switchedOn && (
        <p className="mt-2 text-xs font-semibold text-[#b26a1d]">The AI service isn't switched on yet; the key will work once it is.</p>
      )}

      {row.key ? (
        <p className="mt-2 text-xs text-[#3b4560]">
          Key <span className="font-mono">{row.key.prefix}…</span> · created {fmt(row.key.createdAt)} ·{" "}
          {row.key.lastUsedAt ? `last used ${fmt(row.key.lastUsedAt)}` : "not used yet"} · {row.usedToday}/{row.dailyLimit} today
        </p>
      ) : (
        <p className="mt-2 text-xs text-[#8189a0]">No key yet.</p>
      )}

      {newKey && (
        <div className="mt-3 rounded-2xl border border-[#d6efe2] bg-[#f3fbf7] p-4">
          <p className="text-xs font-bold text-[#2a7a56]">Copy it now: it won't be shown again.</p>
          <div className="mt-2 flex gap-2">
            <code className="flex-1 break-all rounded-xl bg-white px-3 py-2 text-xs text-[#16213e]">{newKey}</code>
            <button type="button" onClick={copy} className="rounded-xl bg-[#2a7a56] px-3 text-xs font-semibold text-white">
              {copied ? <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Copied</> : "Copy"}
            </button>
          </div>
          <p className="mt-2 text-[12.5px] leading-5 text-[#3b4560]">
            {row.firstName} pastes it into their project. The AI address is{" "}
            <span className="font-mono">{API_BASE}/ai/explorer</span>.
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={make}
          disabled={creating}
          className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a] disabled:opacity-50"
        >
          {creating ? "Creating…" : row.key ? "Replace key" : "Create key"}
        </button>
        {row.key && (
          <button
            type="button"
            onClick={drop}
            disabled={removing}
            className="rounded-xl px-3 py-2 text-xs font-semibold text-[#b94a3a] hover:bg-[#fff5f3] disabled:opacity-50"
          >
            Remove
          </button>
        )}
      </div>
      {note && <p className={`mt-2 text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</p>}
    </div>
  );
}
